import type {
    QuestionnaireConditionFragment,
    QuestionnaireQuestionChoiceOptionContentFragment,
    QuestionnaireQuestionChoiceOptionContentIconCardConditionalFragment,
    QuestionnaireQuestionChoiceOptionContentIconCardFragment,
    QuestionnaireQuestionChoiceOptionContentImageCardConditionalFragment,
    QuestionnaireQuestionChoiceOptionContentImageCardFragment,
    QuestionnaireQuestionChoiceOptionContentImageConditionalFragment,
    QuestionnaireQuestionChoiceOptionContentImageFragment,
    QuestionnaireQuestionChoiceOptionContentTextConditionalFragment,
    QuestionnaireQuestionChoiceOptionContentTextFragment,
} from '@/graphql/api'
import { assertNever } from '@/helpers'
import { getAnswer, type QuestionnaireStore } from '../../useQuestionnaireStore'

const conditionPredicate =
    (questionnaire: QuestionnaireStore, questionnaireId: string) =>
    (condition: QuestionnaireConditionFragment) =>
        condition.rules[condition.type === 'OR' ? 'some' : 'every']((rule) => {
            const input =
                questionnaire.answers[questionnaireId]?.[rule.question.id]
                    ?.answer

            if (input === undefined) {
                return false
            }

            const isArray = input.answer.multiChoice !== undefined
            const isNumber = input.answer.number !== undefined
            const theAnswer = getAnswer(input)

            switch (rule.__typename) {
                case 'QuestionnaireConditionIsEmpty':
                    return isArray
                        ? input.answer.multiChoice?.length === 0
                        : !theAnswer
                case 'QuestionnaireConditionIsEqualTo':
                    return isArray
                        ? input.answer.multiChoice?.some(
                              (item) => item.referenceId === rule.valueString
                          )
                        : theAnswer === Number(rule.valueString) ||
                              theAnswer === rule.valueString
                case 'QuestionnaireConditionIsGreaterThan':
                    return isNumber
                        ? input.answer.number?.number &&
                              input.answer.number?.number > rule.value
                        : true
                case 'QuestionnaireConditionIsLessThan':
                    return isNumber
                        ? input.answer.number?.number &&
                              input.answer.number?.number < rule.value
                        : true
                case 'QuestionnaireConditionIsNotEmpty':
                    return isArray
                        ? input.answer.multiChoice?.length
                        : theAnswer || theAnswer === 0
                case 'QuestionnaireConditionNotIsEqualTo':
                    return isArray
                        ? !input?.answer.multiChoice?.some(
                              (item) => item.referenceId === rule.valueString
                          )
                        : theAnswer !== rule.valueString
                case 'QuestionnaireConditionEqualToChoice':
                    return (
                        rule.choice &&
                        (isArray
                            ? input.answer.multiChoice?.some(
                                  (item) => item.referenceId === rule.choice?.id
                              )
                            : theAnswer === Number(rule.choice.id) ||
                              theAnswer === rule.choice.id)
                    )
                default: // show anyway if anything goes wrong?
                    return assertNever(rule, true)
            }
        })

export function evaluateConditions(
    conditions: QuestionnaireConditionFragment[],
    questionnaire: QuestionnaireStore,
    questionnaireId: string
): boolean {
    return (
        !conditions.length ||
        conditions.some(conditionPredicate(questionnaire, questionnaireId))
    )
}

export function getOptionContent(
    card: QuestionnaireQuestionChoiceOptionContentImageFragment,
    questionnaire: QuestionnaireStore,
    questionnaireId: string
):
    | QuestionnaireQuestionChoiceOptionContentImageFragment
    | QuestionnaireQuestionChoiceOptionContentImageConditionalFragment
export function getOptionContent(
    card: QuestionnaireQuestionChoiceOptionContentIconCardFragment,
    questionnaire: QuestionnaireStore,
    questionnaireId: string
):
    | QuestionnaireQuestionChoiceOptionContentIconCardFragment
    | QuestionnaireQuestionChoiceOptionContentIconCardConditionalFragment
export function getOptionContent(
    card: QuestionnaireQuestionChoiceOptionContentImageCardFragment,
    questionnaire: QuestionnaireStore,
    questionnaireId: string
):
    | QuestionnaireQuestionChoiceOptionContentImageCardFragment
    | QuestionnaireQuestionChoiceOptionContentImageCardConditionalFragment
export function getOptionContent(
    card: QuestionnaireQuestionChoiceOptionContentTextFragment,
    questionnaire: QuestionnaireStore,
    questionnaireId: string
):
    | QuestionnaireQuestionChoiceOptionContentTextFragment
    | QuestionnaireQuestionChoiceOptionContentTextConditionalFragment
export function getOptionContent(
    card: QuestionnaireQuestionChoiceOptionContentFragment,
    questionnaire: QuestionnaireStore,
    questionnaireId: string
):
    | QuestionnaireQuestionChoiceOptionContentFragment
    | QuestionnaireQuestionChoiceOptionContentImageConditionalFragment
    | QuestionnaireQuestionChoiceOptionContentIconCardConditionalFragment
    | QuestionnaireQuestionChoiceOptionContentImageCardConditionalFragment
    | QuestionnaireQuestionChoiceOptionContentTextConditionalFragment {
    const predicate = conditionPredicate(questionnaire, questionnaireId)
    const index = card.conditionalContent.findIndex((content) =>
        content.conditions.some(predicate)
    )

    return card.conditionalContent[index] || card
}
